import * as React from 'react';
import {useCallback, useContext, useMemo, useState} from 'react';
import * as HL from './cg/hl';
import * as CG from './cg/graph';
import * as Suggest from './cg/suggest';
import * as CGReact from './cg/cgreact';
import * as Types from './types';
import {ResolverContext} from './cg/resolverContext';

interface ExpressionEditorExternalState {
  node: HL.EditingNode;
  frame: {[argName: string]: CG.Node};
}

interface ExpressionEditorInternalState {
  buffer: string;
  tailOpKey: number;

  // Focus on void, var, or output node means the user is chaining an
  // an op. Focus on a const node means they're editing that const.
  // Future: Focus on an Op means editing an op name.
  focus?: HL.EditingNode;
  cursorPos?: number;

  suggestions: Suggest.AutosuggestResult[];
}

export type ExpressionEditorState = ExpressionEditorExternalState &
  ExpressionEditorInternalState;

export async function updateNodeAndFocus(
  context: ResolverContext,
  state: ExpressionEditorState,
  setState: (newState: ExpressionEditorState) => void,
  newNode: HL.EditingNode
): Promise<void> {
  if (state.focus == null) {
    throw new Error('invalid');
  }
  if (newNode.nodeType === 'void') {
    throw new Error('invalid');
  }
  // First improve the node if we can
  if (newNode.nodeType === 'output') {
    newNode = await HL.refineEditingNodeType(context, newNode);
  }

  const newGraph = await HL.replaceNodeAndUpdateDownstreamTypes(
    context,
    state.node,
    state.focus,
    newNode
  );

  let focus: HL.EditingNode = newNode;
  if (newNode.nodeType === 'output') {
    const inputs = Object.values(newNode.fromOp.inputs);
    const firstVoidIndex = inputs.findIndex(input => input.nodeType === 'void');
    if (firstVoidIndex !== -1) {
      focus = inputs[firstVoidIndex];
    }
  }
  if (focus === newNode) {
    while (focus !== newGraph) {
      const consumer = HL.findConsumingOp(focus, newGraph);
      if (consumer == null) {
        // No more downstream nodes to check
        break;
      }
      if (consumer != null) {
        const opDef = CG.registeredOps[consumer.outputNode.fromOp.name];
        const consumerOutputNode = consumer.outputNode;
        const argNames = Object.keys(consumer.outputNode.fromOp.inputs);
        const argNodes = Object.values(consumer.outputNode.fromOp.inputs);
        const argIndex = argNames.indexOf(consumer.argName);
        if (argIndex === -1) {
          throw new Error('invalid');
        }
        const voidIndex = argNodes.findIndex(
          (n, i) =>
            i >= argIndex &&
            (n.nodeType === 'void' ||
              !Types.isAssignableTo(n.type, opDef.inputTypes[argNames[i]]))
        );
        if (voidIndex !== -1) {
          focus = argNodes[voidIndex];
          break;
        }
        focus = consumerOutputNode;
      }
    }
  }
  // TODO: generalize and clean up races
  const suggestions = await Suggest.autosuggest(
    context,
    focus,
    newGraph,
    state.frame
  );
  setState({
    ...state,
    node: newGraph,
    focus,
    suggestions,
  });
}

export async function focusOnTail(
  context: ResolverContext,
  state: ExpressionEditorState,
  setState: (newState: ExpressionEditorState) => void
): Promise<void> {
  const suggestions = await Suggest.autosuggest(
    context,
    state.node,
    state.node,
    state.frame
  );
  setState({
    ...state,
    focus: state.node,
    suggestions,
  });
}

export async function focusNode(
  context: ResolverContext,
  state: ExpressionEditorState,
  setState: (newState: ExpressionEditorState) => void,
  node: HL.EditingNode
) {
  const suggestions = await Suggest.autosuggest(
    context,
    node,
    state.node,
    state.frame
  );
  if (state.focus !== node) {
    setState({
      ...state,
      focus: node,
      suggestions,
    });
  }
}

export async function blur(
  context: ResolverContext,
  state: ExpressionEditorState,
  setState: (newState: ExpressionEditorState) => void
) {
  setState({
    ...state,
    focus: undefined,
  });
}

export async function deletePrev(
  context: ResolverContext,
  state: ExpressionEditorState,
  setState: (newState: ExpressionEditorState) => void
): Promise<void> {
  if (state.focus == null) {
    return;
  }

  // in the next block, we'll generate a version of the expression where
  // we've performed a deletion (updatedNode) and select the node that
  // should be focused in the editor after deletion is complete (nextFocus)
  let updatedNode = state.node;
  let nextFocus = state.focus;

  const rightMostAncestor = HL.rightMostAncestor(nextFocus);
  const {argIndex, outputNode} =
    HL.findConsumingOp(rightMostAncestor, state.node) || {};

  // which delete behavior we should use depends on what our consumer is,
  // and where we fall in its list of arguments
  if (
    rightMostAncestor.nodeType === 'output' &&
    HL.isChainedUnaryOp(rightMostAncestor.fromOp) &&
    outputNode?.fromOp.name !== 'pick'
  ) {
    // we're a chained unary operator with no args
    // delete the last link in the chain
    // e.g. x["foo"].bar().baz() -> x["foo"].bar()

    nextFocus = Object.values(rightMostAncestor.fromOp.inputs)[0];
    updatedNode = HL.replaceNode(state.node, rightMostAncestor, nextFocus);
  } else if (outputNode == null || argIndex == null) {
    // we have no consumer: focus is the root node, and can be deleted directly
    nextFocus = CG.voidNode();
    updatedNode = HL.replaceNode(state.node, rightMostAncestor, nextFocus);
  } else {
    const argNodes = Object.values(outputNode.fromOp.inputs);

    if (HL.isBinaryOp(outputNode.fromOp)) {
      // our consumer is a binary infix operator
      if (argIndex === 1) {
        // we're the right child
        if (nextFocus.nodeType !== 'void') {
          // we're NOT already void, so void us out
          // e.g. 3 + 4 -> 3 + _
          nextFocus = CG.voidNode();
          updatedNode = HL.replaceNode(
            state.node,
            rightMostAncestor,
            nextFocus
          );
        } else {
          // replace the binary operator with the left child
          // e.g. 3 + 4 -> 3
          nextFocus = argNodes[0];
          updatedNode = HL.replaceNode(state.node, outputNode, nextFocus);
        }
      } else {
        // we're the left child
        if (nextFocus.nodeType !== 'void') {
          // we're NOT already void, so void us out
          // e.g. 3 + 4 -> _ + 4
          nextFocus = CG.voidNode();
          updatedNode = HL.replaceNode(
            state.node,
            rightMostAncestor,
            nextFocus
          );
        } else {
          // we are already void, so delete the entire binary operator
          // e.g. _ + 4 -> _
          nextFocus = CG.voidNode();
          updatedNode = HL.replaceNode(state.node, outputNode, nextFocus);
        }
      }
    } else if (HL.isChainedUnaryOp(outputNode.fromOp)) {
      // our consumer is a chained unary operator

      if (outputNode.fromOp.name === 'pick') {
        // our consumer is pick (x["foo"]), which is a special case:
        // regardless of which argument we are, erase the entire op
        // e.g. x["foo"] -> _
        nextFocus = CG.voidNode();
        updatedNode = HL.replaceNode(state.node, outputNode, nextFocus);
      } else {
        // our consumer is a normal chained unary operator (x.foo(a,b))

        if (
          argIndex === 0 ||
          (argIndex === 1 && rightMostAncestor.nodeType === 'void')
        ) {
          // we are the first argument (the thing on which we're chaining),
          // OR, we're the first thing inside the parens and we've already been deleted
          // either way: that means we're trying to delete this link off the chain
          // replace us with the thing we chained off of
          // e.g. x.foo() -> x
          // e.g. x.foo(_, 3) -> x (where the cursor is at the _)
          nextFocus = argNodes[0];
          updatedNode = HL.replaceNode(state.node, outputNode, nextFocus);
        } else {
          // we're not the first argument (so, we're something inside the parens)
          // delete us
          // e.g. x.foo(1, 2) -> x.foo(1, _)
          // e.g. x.foo(1) -> x.foo(_)
          nextFocus = CG.voidNode();
          updatedNode = HL.replaceNode(
            state.node,
            rightMostAncestor,
            nextFocus
          );

          if (argIndex > 1) {
            // we're not the first thing inside the parentheses, so move the cursor
            // one arg to the left.

            // to see the intention, consider:
            // x.foo(1, 2)
            // if you delete the 2, you should end up focused on the 1 -- but
            // if you delete the 1, you should STAY focused on where the 1 used
            // to be until you delete again and kill the whole chaining
            // operator.

            // this way, if you change your mind -- decide to add the 1 back in --
            // you'll be able to type and replace the thing you just got rid of.
            // if we moved the focus to arg 0, you'd be outside the parens and would
            // have to navigate back in
            nextFocus = argNodes[argIndex - 1];
          }
        }
      }
    } else {
      // our consumer is a function call

      if (argIndex !== 0) {
        // either we're not the first argument, OR
        // we're the first argument and we're non void,
        // delete us
        // e.g. function(3, 2) -> function(3, _) -> function(_, _)
        nextFocus = CG.voidNode();
        updatedNode = HL.replaceNode(state.node, nextFocus, CG.voidNode());

        if (argIndex > 1) {
          // same as in unary operator branch above; shift one to the left
          nextFocus = argNodes[argIndex - 1];
        }
      } else {
        // we're the first argument AND already void
        // delete the function call operation
        // e.g. function(_, _) -> _
        nextFocus = CG.voidNode();
        updatedNode = HL.replaceNode(state.node, outputNode, nextFocus);
      }
    }
  }

  // TODO: Not yet fixing up graph types with improveOutputNode here... do we
  // need to? Remember its tricky because we need to ensure focus points to
  // the right place after that operation.

  const suggestions = await Suggest.autosuggest(
    context,
    nextFocus,
    updatedNode,
    state.frame
  );

  setState({
    ...state,
    node: updatedNode,
    focus: nextFocus,
    suggestions,
  });
}

export async function setBuffer(
  context: ResolverContext,
  state: ExpressionEditorState,
  setState: (newState: ExpressionEditorState) => void,
  newBuffer: string
) {
  if (state.focus == null) {
    throw new Error('invalid');
  }
  let newTail = state.node;
  let newFocus = state.focus;
  let modifiedGraph = false;
  let cursorPos: number | undefined;
  const consumer = HL.findConsumingOp(state.focus, state.node);
  if (consumer != null) {
    const opDef = CG.registeredOps[consumer.outputNode.fromOp.name];
    const argType = opDef.inputTypes[consumer.argName];
    const isNumberArg = Types.isAssignableTo('number', argType);

    if (state.focus.nodeType === 'void' && isNumberArg) {
      // Swap to a const number node if the user starts typing a number
      const matchNum = newBuffer.match(/^\d/);
      if (matchNum != null) {
        const newConstNode = HL.constNumber(parseFloat(matchNum[0]));
        newTail = await HL.refineEditingNodeType(
          context,
          HL.replaceNode(state.node, state.focus, newConstNode)
        );
        newFocus = newConstNode;
        cursorPos = 1;
        modifiedGraph = true;
      }
    } else if (
      // Swap back to a void node if user deletes a number node
      state.focus.nodeType === 'const' &&
      isNumberArg &&
      newBuffer === ''
    ) {
      const newVoidNode = CG.voidNode();
      newTail = await HL.refineEditingNodeType(
        context,
        HL.replaceNode(state.node, state.focus, newVoidNode)
      );
      newFocus = newVoidNode;
      modifiedGraph = true;
    }
  }
  if (!modifiedGraph) {
    // Automatically select suggestion if we have an exact match.
    const suggestionMatch = state.suggestions.find(
      sugg => sugg.suggestionString.trim() === newBuffer
    );
    if (suggestionMatch != null) {
      updateNodeAndFocus(context, state, setState, suggestionMatch.newNode);
      return;
    }
  }

  setState({
    ...state,
    node: newTail,
    focus: newFocus,
    buffer: newBuffer,
    cursorPos,
  });
}

interface EEContextState {
  state: ExpressionEditorState;
  setState(newState: ExpressionEditorState): void;
}

const EEContext = React.createContext<EEContextState | undefined>(undefined);

export const EEContextProvider: React.FC<{
  node: HL.EditingNode;
  frame?: {[argName: string]: CG.Node};
  updateNode(newNode: HL.EditingNode): void;
}> = ({node, frame, updateNode, children}) => {
  const [state, setState] = useState<ExpressionEditorInternalState>({
    buffer: '',
    tailOpKey: 0,
    suggestions: [],
  });
  const setStateAndUpdateNode = useCallback(
    (newState: ExpressionEditorState) => {
      setState(newState);
      updateNode(newState.node as any);
    },
    [updateNode]
  );
  const fullState = useMemo(() => ({...state, node, frame: frame ?? {}}), [
    state,
    node,
    frame,
  ]);

  const contextValue = useMemo(
    () => ({
      state: fullState,
      setState: setStateAndUpdateNode,
    }),
    [fullState, setStateAndUpdateNode]
  );

  return (
    <EEContext.Provider value={contextValue}> {children} </EEContext.Provider>
  );
};

export const useAction = <T extends any[], R>(
  fn: (
    context: ResolverContext,
    state: ExpressionEditorState,
    setState: (newState: ExpressionEditorState) => void,
    ...rest: T
  ) => R
): ((...args: T) => R) => {
  const fnWithCGContext = CGReact.useContextBound(fn as any);
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  const {state, setState} = context;
  return useCallback(
    (...args: T) => fnWithCGContext(state, setState, ...args),
    [fnWithCGContext, state, setState]
  ) as any;
};

export const useFocusedNode = () => {
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  return context.state.focus;
};

export const useCursorPos = () => {
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  return context.state.cursorPos;
};

export const useTailNode = () => {
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  return context.state.node;
};

export const useTailOpKey = () => {
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  return context.state.tailOpKey;
};

export const useBuffer = () => {
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  return context.state.buffer;
};

export const useFrame = () => {
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  return context.state.frame;
};

export const useSuggestions = () => {
  const context = useContext(EEContext);
  if (context == null) {
    throw new Error('EE context not initialized');
  }
  return context.state.suggestions;
};
